import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { authAPI, setApiToken, setAuthCheckingState, clearApiToken } from '../services/api';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

const AuthContext = createContext(null);

// Storage helper - writes to BOTH Preferences + localStorage + cookie for maximum persistence
const storage = {
  async get(key) {
    // Try Capacitor Preferences first (most reliable on native)
    if (Capacitor.isNativePlatform()) {
      try {
        const { value } = await Preferences.get({ key });
        if (value) return value;
      } catch (e) {
        console.warn('Preferences.get failed:', e);
      }
    }
    // Fallback to localStorage
    try {
      const val = localStorage.getItem(key);
      if (val) return val;
    } catch (e) {
      // localStorage might not be available
    }
    return null;
  },
  async set(key, value) {
    // Write to Capacitor Preferences (reliable on native)
    if (Capacitor.isNativePlatform()) {
      try {
        await Preferences.set({ key, value });
      } catch (e) {
        console.warn('Preferences.set failed:', e);
      }
    }
    // Always also write to localStorage
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      console.warn('localStorage.set failed:', e);
    }
  },
  async remove(key) {
    if (Capacitor.isNativePlatform()) {
      try {
        await Preferences.remove({ key });
      } catch (e) {
        console.warn('Preferences.remove failed:', e);
      }
    }
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.warn('localStorage.remove failed:', e);
    }
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);

  useEffect(() => {
    const checkAuth = async () => {
      // Tell api.js interceptor: we are initializing, don't clear tokens on 401
      setAuthCheckingState(true);
      
      try {
        // Read token from storage (Preferences on native, localStorage on web)
        const storedToken = await storage.get('token');
        
        if (!storedToken) {
          console.log('[Auth] No stored token found');
          setLoading(false);
          setAuthCheckingState(false);
          return;
        }
        
        // Set the token in api.js cache BEFORE making the API call
        setApiToken(storedToken);
        setToken(storedToken);
        
        try {
          const response = await authAPI.getMe();
          setUser(response.data.user);
          // Refresh cached user data
          await storage.set('user', JSON.stringify(response.data.user));
          console.log('[Auth] Restored session successfully');
        } catch (error) {
          const status = error.response?.status;
          
          if (status === 401 || status === 403) {
            // Token is truly invalid/expired - NOW we clear everything
            console.error('[Auth] Token expired/invalid, clearing auth');
            setToken(null);
            clearApiToken();
            await storage.remove('token');
            await storage.remove('user');
          } else {
            // Network error or server down - use cached user data, keep token
            console.warn('[Auth] Network error during auth check, using cached data');
            const cachedUser = await storage.get('user');
            if (cachedUser) {
              try {
                setUser(JSON.parse(cachedUser));
              } catch (parseError) {
                // Corrupted cached data
                setToken(null);
                clearApiToken();
                await storage.remove('token');
                await storage.remove('user');
              }
            }
            // Keep the token even without cached user - it might work later when network returns
          }
        }
      } catch (error) {
        console.error('[Auth] Storage error:', error);
      }
      
      setAuthCheckingState(false);
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (identifier, password, totp_code = null) => {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Africa/Abidjan';
      const payload = { identifier, password, timezone };
      if (totp_code) payload.totp_code = totp_code;
      const response = await authAPI.login(payload);
      
      // Check if 2FA is required
      if (response.data.requires_2fa) {
        return { success: false, requires_2fa: true };
      }
      
      const { user: userData, token: newToken } = response.data;
      
      setUser(userData);
      setToken(newToken);
      setApiToken(newToken);
      
      // Persist token and user data in all storage mechanisms
      await storage.set('token', newToken);
      await storage.set('user', JSON.stringify(userData));
      
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed'
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);
      const { user: newUser, token: newToken, recoveryCode } = response.data;
      
      setUser(newUser);
      setToken(newToken);
      setApiToken(newToken);
      
      await storage.set('token', newToken);
      await storage.set('user', JSON.stringify(newUser));
      
      return { success: true, recoveryCode: recoveryCode || null };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Registration failed'
      };
    }
  };

  const logout = async () => {
    setUser(null);
    setToken(null);
    clearApiToken();
    await storage.remove('token');
    await storage.remove('user');
  };

  const updateUser = async (updatedUser) => {
    setUser(updatedUser);
    await storage.set('user', JSON.stringify(updatedUser));
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        login,
        register,
        logout,
        updateUser,
        isAuthenticated: !!token,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
